// client\src\hooks\useAuth.js

import { useState, useEffect, useCallback, useRef } from 'react';
import { callHistoryService } from '../services/callHistoryService';
import { clearAccessTokenExpiry, attemptTokenRefresh } from '../services/apiClient';

const SESSION_CHANNEL_NAME = 'bozarth-session-sync';

export const useAuth = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isReceptionist, setIsReceptionist] = useState(false);
  const [isAuthLoading, setIsAuthLoading] = useState(true);
  const channelRef = useRef(null);

  // ─── Verify session cookie with the server ───
  const verifySession = useCallback(async () => {
    const verifyUrl = window.location.hostname === 'localhost'
      ? 'http://localhost:7080/api/verify'
      : '/api/verify';

    try {
      const response = await fetch(verifyUrl, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.user) {
          return { valid: true, user: data.user };
        }
      }

      // Cookie is present but expired — try a refresh
      if (response.status === 401 || response.status === 403) {
        try {
          await attemptTokenRefresh();
          const retryResponse = await fetch(verifyUrl, {
            method: 'GET',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
          });
          if (retryResponse.ok) {
            const retryData = await retryResponse.json();
            if (retryData.success && retryData.user) {
              return { valid: true, user: retryData.user };
            }
          }
        } catch {
          // refresh failed — session is truly dead
        }
      }

      return { valid: false };
    } catch {
      // Network error — can't verify, fall back to cached state
      return { valid: null };
    }
  }, []);

  // Load user from storage on mount, then verify with server
  useEffect(() => {
    const initAuth = async () => {
      setIsAuthLoading(true);

      // Step 1: Try localStorage for quick hydration
      const savedUser = localStorage.getItem('currentUser');
      let cachedUser = null;
      if (savedUser) {
        try {
          cachedUser = JSON.parse(savedUser);
          if (!cachedUser.role) {
            console.log('⚠️ Old session detected without role information');
            cachedUser = null;
            localStorage.removeItem('currentUser');
          }
        } catch (error) {
          console.error('Error parsing saved user:', error);
          localStorage.removeItem('currentUser');
        }
      }

      // Step 2: Verify with server (cookie is the source of truth)
      const result = await verifySession();

      if (result.valid === true) {
        // Server confirmed session — use server user data, update cache
        const user = {
          username: result.user.username,
          role: result.user.role || cachedUser?.role || 'standard',
          roleDetails: result.user.roleDetails || cachedUser?.roleDetails,
          // /verify hands back the JWT payload, which carries the id inside
          // roleDetails. The cached copy is the last fallback for sessions
          // minted before this field existed.
          ClickToCallId:
            result.user.ClickToCallId ??
            result.user.roleDetails?.ClickToCallId ??
            cachedUser?.ClickToCallId ??
            null,
        };
        localStorage.setItem('currentUser', JSON.stringify(user));
        setCurrentUser(user);
        setIsAuthenticated(true);
        setIsReceptionist(user.role === 'receptionists');
        console.log('✅ Session verified with server for:', user.username);
      } else if (result.valid === false) {
        // Server explicitly rejected session — clear everything
        localStorage.removeItem('currentUser');
        setIsAuthenticated(false);
        setCurrentUser(null);
        setIsReceptionist(false);
        console.log('🔒 Session invalid — cleared auth state');
      } else {
        // Network error — trust cached state if available
        if (cachedUser) {
          setCurrentUser(cachedUser);
          setIsAuthenticated(true);
          setIsReceptionist(cachedUser.role === 'receptionists');
          console.log('⚠️ Offline — using cached auth for:', cachedUser.username);
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
          setIsReceptionist(false);
        }
      }

      setIsAuthLoading(false);
    };

    initAuth();

    // ─── Primary: Electron IPC for cross-window sync ───
    if (window.electronAPI && window.electronAPI.onSessionChanged) {
      window.electronAPI.onSessionChanged((data) => {
        const { type, user } = data || {};

        if (type === 'session-logout') {
          console.log('📡 [IPC] Received logout from another Electron window');
          clearAccessTokenExpiry();
          localStorage.removeItem('currentUser');
          setIsAuthenticated(false);
          setCurrentUser(null);
          setIsReceptionist(false);
        } else if (type === 'session-login' && user) {
          console.log('📡 [IPC] Received login from another Electron window:', user.username);
          localStorage.setItem('currentUser', JSON.stringify(user));
          setCurrentUser(user);
          setIsAuthenticated(true);
          setIsReceptionist(user.role === 'receptionists');
        }
      });
    }

    // ─── Fallback: BroadcastChannel for non-Electron (browser tabs) ───
    try {
      const channel = new BroadcastChannel(SESSION_CHANNEL_NAME);
      channelRef.current = channel;

      channel.onmessage = (event) => {
        const { type, user } = event.data || {};

        if (type === 'session-logout') {
          console.log('📡 [BC] Received logout broadcast from another tab');
          clearAccessTokenExpiry();
          localStorage.removeItem('currentUser');
          setIsAuthenticated(false);
          setCurrentUser(null);
          setIsReceptionist(false);
        } else if (type === 'session-login' && user) {
          console.log('📡 [BC] Received login broadcast from another tab:', user.username);
          localStorage.setItem('currentUser', JSON.stringify(user));
          setCurrentUser(user);
          setIsAuthenticated(true);
          setIsReceptionist(user.role === 'receptionists');
        }
      };
    } catch (e) {
      console.warn('⚠️ BroadcastChannel not available:', e.message);
    }

    // Legacy storage sync (fallback)
    const handleStorageChange = (e) => {
      if (e.key === 'currentUser') {
        if (e.newValue) {
          try {
            const user = JSON.parse(e.newValue);
            setCurrentUser(user);
            setIsAuthenticated(true);
            setIsReceptionist(user.role === 'receptionists');
          } catch { /* ignore parse errors */ }
        } else {
          setIsAuthenticated(false);
          setCurrentUser(null);
          setIsReceptionist(false);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
    };
  }, [verifySession]);


  // Handle successful login
  const handleLogin = useCallback((user) => {
    setCurrentUser(user);
    setIsAuthenticated(true);
    
    const isReceptionistUser = user.role === 'receptionists';
    setIsReceptionist(isReceptionistUser);
    
    console.log('🎭 SET RECEPTIONIST STATE TO:', isReceptionistUser);

    // Primary: Electron IPC broadcast
    if (window.electronAPI && window.electronAPI.notifySessionChanged) {
      window.electronAPI.notifySessionChanged('session-login', user);
    }

    // Fallback: BroadcastChannel for browser tabs
    try {
      if (channelRef.current) {
        channelRef.current.postMessage({ type: 'session-login', user });
      }
    } catch { /* BroadcastChannel may be closed */ }
    
    return { success: true, user, isReceptionist: isReceptionistUser };
  }, []);

  // Handle logout
  const handleLogout = useCallback(async () => {
    console.log('👋 Logging out...');

    try {
      await callHistoryService.logout();
    } catch (error) {
      console.error('Logout API error:', error);
    }

    clearAccessTokenExpiry();
    localStorage.removeItem('currentUser');

    // Primary: Electron IPC broadcast
    if (window.electronAPI && window.electronAPI.notifySessionChanged) {
      window.electronAPI.notifySessionChanged('session-logout', null);
    }

    // Fallback: BroadcastChannel for browser tabs
    try {
      if (channelRef.current) {
        channelRef.current.postMessage({ type: 'session-logout' });
      }
    } catch { /* BroadcastChannel may be closed */ }

    setIsAuthenticated(false);
    setCurrentUser(null);
    setIsReceptionist(false);

    console.log('✅ Logout complete');

    return { success: true };
  }, []);

  return {
    // State
    isAuthenticated,
    currentUser,
    isReceptionist,
    isAuthLoading,
    
    // Functions
    handleLogin,
    handleLogout
  };
};